import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { dashboardAPI } from '@/lib/api';
import {
    Trophy, TrendingUp, CheckCircle2, XCircle, Users, Loader2, Gift, Crown, Info,
    ChevronDown, ChevronUp,
} from 'lucide-react';

const fmtEur = (v) => `${(Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
const MONTH_LABEL = (ym) => {
    if (!ym) return '';
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const CRITERIA_ORDER = [
    'effectif_actif', 'production_equipe', 'qualite_crm_relance', 'qualite_crm_implication',
    'reunion_mensuelle', 'implication_perso', 'discipline', 'animation',
];

const STORAGE_KEY = 'valnexo_prime_secteur_expanded';

export function PrimeSecteurWidget({ vpId, sectorName, defaultExpanded = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [expanded, setExpanded] = useState(() => {
        if (defaultExpanded) return true;
        try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
    });
    const [history, setHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    const toggle = () => {
        setExpanded((prev) => {
            const next = !prev;
            try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* noop */ }
            return next;
        });
    };

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(false);
        dashboardAPI.getPrimeSecteur(vpId ? { vp_id: vpId } : {})
            .then((res) => { if (alive) setData(res.data); })
            .catch(() => { if (alive) setError(true); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [vpId]);

    // Charge l'historique mensuel à la première ouverture (lazy)
    useEffect(() => {
        if (!expanded || history !== null || historyLoading) return;
        setHistoryLoading(true);
        const params = { months: 6, ...(vpId ? { vp_id: vpId } : {}) };
        dashboardAPI.getPrimeSecteurHistory(params)
            .then((res) => setHistory(res.data?.history || []))
            .catch(() => setHistory([]))
            .finally(() => setHistoryLoading(false));
    }, [expanded, vpId, history, historyLoading]);

    if (loading) {
        return (
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50" data-testid="prime-secteur-widget">
                <CardContent className="py-4 flex items-center justify-center gap-2 text-slate-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Calcul de la prime de secteur…
                </CardContent>
            </Card>
        );
    }
    if (error || !data) return null;

    const ca = data.ca_ep_total || 0;
    const palier = data.palier || 0;
    const nextSeuil = data.next_seuil;
    const reste = data.reste_next || 0;
    const lowerBound = palier || 0;
    const upperBound = nextSeuil || (palier || 8000);
    const pct = nextSeuil
        ? Math.min(100, Math.max(0, ((ca - lowerBound) / (upperBound - lowerBound)) * 100))
        : 100;
    const respDue = data.prime_responsable_due;
    const eligibles = (data.apporteurs || []).filter((a) => a.eligible);
    const criteriaOkCount = Object.values(data.criteria || {}).filter((c) => c.ok).length;
    const criteriaTotal = Object.keys(data.criteria || {}).length;

    return (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 overflow-hidden" data-testid="prime-secteur-widget">
            {/* ===== Barre compacte (toujours visible) ===== */}
            <button
                type="button"
                onClick={toggle}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-amber-50/60 transition-colors"
                data-testid="prime-toggle"
                aria-expanded={expanded}
            >
                <div className="p-2 rounded-full bg-amber-100 shrink-0">
                    <Trophy className="w-5 h-5 text-amber-500" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">
                            {sectorName ? `Secteur ${sectorName}` : 'Prime de secteur — Courtage'}
                        </span>
                        <Badge variant="outline" className="bg-white text-amber-700 border-amber-200 capitalize text-[10px] py-0" data-testid="prime-month">
                            {MONTH_LABEL(data.month)}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-x-4 gap-y-0.5 flex-wrap mt-0.5 text-xs text-slate-600">
                        <span data-testid="prime-ca-compact">
                            CA <strong className="text-slate-900">{fmtEur(ca)}</strong>
                        </span>
                        <span>
                            Palier <strong className="text-amber-700">{palier > 0 ? fmtEur(palier) : 'Aucun'}</strong>
                        </span>
                        {nextSeuil ? (
                            <span className="flex items-center gap-1" data-testid="prime-reste-compact">
                                <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
                                reste <strong className="text-amber-700">{fmtEur(reste)}</strong> pour le palier suivant
                            </span>
                        ) : (
                            <span className="text-emerald-700 font-medium">🏆 Palier max</span>
                        )}
                    </div>
                </div>
                {/* Statut prime responsable (pastille) */}
                <span className={`shrink-0 hidden sm:inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full ${respDue ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    <Crown className="w-3 h-3" />
                    {respDue ? 'Prime resp. due' : `Resp. ${criteriaOkCount}/${criteriaTotal}`}
                </span>
                <span className="shrink-0 text-slate-400">
                    {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </span>
            </button>

            {/* ===== Détail (agrandi) ===== */}
            {expanded && (
                <CardContent className="space-y-5 pt-0 pb-5">
                    <p className="text-xs text-slate-500 border-t border-amber-100 pt-3">
                        CA global (courtage) des dossiers Courtage passés en « gagné » ce mois par vos apporteurs (VP direct exclu).
                    </p>

                    {/* CA + palier + progression */}
                    <div className="rounded-xl border border-amber-200 bg-white p-4">
                        <div className="flex items-end justify-between gap-3 flex-wrap">
                            <div>
                                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">CA du mois</p>
                                <p className="text-3xl font-bold text-slate-900" data-testid="prime-ca">{fmtEur(ca)}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{data.won_count} dossier{data.won_count > 1 ? 's' : ''} gagné{data.won_count > 1 ? 's' : ''}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Palier de prime validé</p>
                                <p className="text-2xl font-bold text-amber-600" data-testid="prime-palier">
                                    {palier > 0 ? fmtEur(palier) : 'Aucun'}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="h-2.5 rounded-full bg-amber-100 overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            {nextSeuil ? (
                                <p className="text-xs text-slate-600 mt-2 flex items-center gap-1.5" data-testid="prime-reste">
                                    <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
                                    Reste <strong className="text-amber-700">{fmtEur(reste)}</strong> pour atteindre le palier suivant ({fmtEur(nextSeuil)})
                                </p>
                            ) : (
                                <p className="text-xs text-emerald-700 mt-2 font-medium">🏆 Palier maximum atteint !</p>
                            )}
                        </div>
                    </div>

                    {/* Deux enveloppes */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="prime-apporteurs-box">
                            <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-medium flex items-center gap-1">
                                <Gift className="w-3.5 h-3.5" /> Prime Apporteurs (enveloppe)
                            </p>
                            <p className="text-xl font-bold text-emerald-800 mt-1">{fmtEur(data.prime_apporteurs_total)}</p>
                            <p className="text-xs text-emerald-700/80 mt-0.5">
                                partagée entre {eligibles.length} apporteur{eligibles.length > 1 ? 's' : ''} éligible{eligibles.length > 1 ? 's' : ''}
                            </p>
                        </div>
                        <div className={`rounded-xl border p-3 ${respDue ? 'border-purple-200 bg-purple-50/60' : 'border-slate-200 bg-slate-50'}`} data-testid="prime-responsable-box">
                            <p className={`text-[11px] uppercase tracking-wider font-medium flex items-center gap-1 ${respDue ? 'text-purple-700' : 'text-slate-500'}`}>
                                <Crown className="w-3.5 h-3.5" /> Prime Responsable
                            </p>
                            <p className={`text-xl font-bold mt-1 ${respDue ? 'text-purple-800' : 'text-slate-400 line-through'}`}>
                                {fmtEur(data.prime_responsable_total)}
                            </p>
                            <p className="text-xs mt-0.5">
                                {respDue ? (
                                    <span className="text-purple-700 font-medium">✓ Tous les critères validés — prime due</span>
                                ) : (
                                    <span className="text-rose-600 font-medium">Critères non remplis — prime non due</span>
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Checklist critères */}
                    <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                            <Info className="w-3.5 h-3.5 text-slate-400" />
                            Critères pour débloquer la Prime Responsable ({criteriaOkCount}/{criteriaTotal})
                        </p>
                        <div className="grid grid-cols-1 gap-1.5" data-testid="prime-criteria">
                            {CRITERIA_ORDER.map((key) => {
                                const c = data.criteria?.[key];
                                if (!c) return null;
                                return (
                                    <div key={key} className="flex items-start justify-between gap-3 text-sm rounded-lg bg-white border border-slate-100 px-3 py-1.5" data-testid={`prime-criterion-${key}`}>
                                        <span className="flex items-start gap-2 text-slate-700">
                                            {c.ok
                                                ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                                : <XCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />}
                                            <span>{c.label}</span>
                                        </span>
                                        <span className={`shrink-0 text-xs font-medium ${c.ok ? 'text-emerald-600' : 'text-rose-500'}`}>
                                            {c.value === null || c.value === undefined ? '—' : c.value}
                                            {typeof c.target === 'number' ? ` / ${c.target}` : ` (${c.target})`}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Apporteurs éligibles */}
                    <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-slate-400" />
                            Apporteurs éligibles au partage ({eligibles.length}/{data.apporteurs_count})
                        </p>
                        {(data.apporteurs || []).length === 0 ? (
                            <p className="text-xs text-slate-400 py-2">Aucun apporteur dans le secteur.</p>
                        ) : (
                            <div className="space-y-1.5" data-testid="prime-eligible-list">
                                {data.apporteurs.map((a) => (
                                    <div
                                        key={a.user_id}
                                        className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${a.eligible ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-100 bg-white'}`}
                                        data-testid={`prime-apporteur-${a.user_id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                                                {a.eligible
                                                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                                    : <XCircle className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                                                {a.name}
                                            </p>
                                            <p className="text-[11px] text-slate-400 mt-0.5">
                                                CA {fmtEur(a.ca_ep)} · {a.commissions_validees} com. validée{a.commissions_validees > 1 ? 's' : ''} · implication {a.implication ?? '—'}
                                            </p>
                                        </div>
                                        {a.eligible && a.prime_part > 0 && (
                                            <span className="shrink-0 font-bold text-emerald-700" data-testid={`prime-part-${a.user_id}`}>
                                                {fmtEur(a.prime_part)}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Historique mensuel (progression) */}
                    <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                            <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
                            Progression des 6 derniers mois
                        </p>
                        {historyLoading ? (
                            <div className="py-3 flex items-center gap-2 text-xs text-slate-400">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement de l'historique…
                            </div>
                        ) : (history && history.length > 0) ? (
                            <div className="flex items-end gap-2 sm:gap-3" data-testid="prime-history">
                                {(() => {
                                    const maxCa = Math.max(1, ...history.map((h) => h.ca_ep_total || 0));
                                    return history.map((h) => {
                                        const isCurrent = h.month === data.month;
                                        const barPct = Math.round(((h.ca_ep_total || 0) / maxCa) * 100);
                                        const [hy, hm] = h.month.split('-').map(Number);
                                        const short = new Date(hy, hm - 1, 1).toLocaleDateString('fr-FR', { month: 'short' });
                                        return (
                                            <div key={h.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`prime-history-${h.month}`} title={`${fmtEur(h.ca_ep_total)} · palier ${h.palier > 0 ? fmtEur(h.palier) : '—'}`}>
                                                <span className="text-[10px] font-medium text-slate-500">{h.ca_ep_total > 0 ? fmtEur(h.ca_ep_total) : '—'}</span>
                                                <div className="w-full h-20 flex items-end rounded-md bg-slate-100 overflow-hidden">
                                                    <div
                                                        className={`w-full rounded-t-md transition-all ${isCurrent ? 'bg-gradient-to-t from-amber-500 to-orange-400' : 'bg-amber-200'}`}
                                                        style={{ height: `${Math.max(4, barPct)}%` }}
                                                    />
                                                </div>
                                                <span className={`text-[10px] capitalize ${isCurrent ? 'font-bold text-amber-700' : 'text-slate-400'}`}>{short}</span>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 py-2">Aucun historique disponible.</p>
                        )}
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

export default PrimeSecteurWidget;
